import { useState } from 'react';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { Button, Field } from '../../components/ui';

/** Matches `ONU_NOTE_MAX` on the API, so the cap is met at the keyboard. */
const NOTE_MAX = 4000;

/**
 * The operator's own note about a subscriber.
 *
 * Sibling of the rename pencil beside it, and deliberately not the same thing.
 * A rename is written to the chassis, because the name lives there and the next
 * inventory sweep would overwrite a local one. A note lives nowhere but here:
 * no OLT has a field for "gate code 4417, dog", and none needs one — so this
 * saves to the platform and contacts nothing.
 *
 * Its other job is quieter. The SmartOLT import fills this field when SimpleACS
 * holds nothing and reports a conflict rather than overwriting one, so the note
 * saved here is what stops SmartOLT's version being written over it later.
 *
 * Saving closes the dialog at once and the write runs in the tasks tray, like
 * every other modal write on this platform; the tray's toast is the receipt.
 */
export function NoteDialog({
  onuId,
  currentNote,
  serial,
  onClose,
}: {
  onuId: string;
  currentNote: string | null;
  serial: string;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [note, setNote] = useState(currentNote ?? '');

  const trimmed = note.trim();
  const had = Boolean(currentNote?.trim());

  const save = () => {
    // Snapshotted: the dialog is gone by the time the write settles.
    const next = trimmed;
    onClose();
    tasks.start({
      label: `${next === '' ? 'Clear' : 'Save'} note — ${serial}`,
      run: () =>
        api.patch(`/onus/${encodeURIComponent(onuId)}/notes`, {
          // Blank clears rather than storing an empty string — the same thing the
          // API does with it, sent explicitly so the intent is in the request.
          notes: next === '' ? null : next,
        }),
      done: () =>
        next === ''
          ? `Note on ${serial} cleared. SmartOLT may fill it again on the next import.`
          : `Note on ${serial} saved — kept here only, nothing was sent to the OLT.`,
      invalidate: [['onu']],
    });
  };

  const unchanged = trimmed === (currentNote ?? '').trim();

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {had ? 'Edit the note on' : 'Add a note to'} {serial}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Whatever the next person needs to know about this subscriber. Kept in SimpleACS
            only &mdash; nothing here is sent to the OLT, and the subscriber&rsquo;s service is
            untouched.
          </p>
        </header>

        <div className="p-5">
          <Field
            label="Note"
            hint={`Up to ${NOTE_MAX.toLocaleString()} characters. Clearing it lets a SmartOLT import fill it again; leaving something here makes that import report a conflict instead of overwriting you.`}
          >
            <textarea
              className="input min-h-[140px] w-full"
              value={note}
              autoFocus
              maxLength={NOTE_MAX}
              placeholder="Access, history, who to call — anything the next engineer on this drop would want to know."
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                // Enter makes paragraphs in a note, so only Escape is a shortcut.
                if (e.key === 'Escape') onClose();
              }}
            />
          </Field>
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-line px-5 py-3">
          <span className="text-xs text-faint tabular-nums">
            {trimmed.length.toLocaleString()} / {NOTE_MAX.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={unchanged} onClick={save}>
              {trimmed === '' && had ? 'Clear' : 'Save'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
